import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { REACT_APP_API_URL } from '../../../../../env';

const SettlementBlock = () => {
  const { ReleaseId } = useParams();
  const [amount, setAmount] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [remarks, setRemarks] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPaymentHistory();
  }, []);

  const fetchPaymentHistory = async () => {
    try {
      const response = await axios.get(
        `${REACT_APP_API_URL}api/dashboard/admin/hotel-blocking/payment-history/${ReleaseId}`
      );
      console.log('Payment history:', response.data.data);
      setPaymentHistory(response.data.data);
    } catch (error) {
      console.error('Error fetching payment history:', error.message);
      setMessage({
        text: 'Failed to load payment history. Please try again.',
        type: 'danger'
      });
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
  };

  const handleFileChange = (e) => {
    setAttachment(e.target.files[0]);
  };

  const updateSettlement = async (e) => {
    e.preventDefault();
    
    if (!amount || amount <= 0) {
      setMessage({ text: 'Please enter a valid amount', type: 'danger' });
      return;
    }

    if (!serialNumber) {
      setMessage({ text: 'Please enter a serial number', type: 'danger' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const formData = new FormData();
      formData.append('PaymentAmount', amount);
      formData.append('Remarks', remarks);
      formData.append('SerialNumber', serialNumber);
      
      if (attachment) {
        formData.append('attachment', attachment);
      }

      const response = await axios.patch(
        `${REACT_APP_API_URL}api/dashboard/admin/hotel-blocking/update/${ReleaseId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('Payment response:', response.data);
      
      setAmount('');
      setSerialNumber('');
      setRemarks('');
      setAttachment(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      setMessage({ 
        text: 'Payment added successfully!', 
        type: 'success' 
      });
      
      fetchPaymentHistory();
      
    } catch (error) {
      console.error('Error updating settlement:', error);
      setMessage({ 
        text: `Error: ${error.response?.data?.message || error.message}`, 
        type: 'danger' 
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalPaidAmount = () => {
    if (!paymentHistory || paymentHistory.length === 0) return 0;
    
    return paymentHistory.reduce((total, payment) => {
      return total + parseFloat(payment.amount_paid || 0);
    }, 0);
  };

  const getTotalAmount = () => {
    if (!paymentHistory || paymentHistory.length === 0) return 0;
    return parseFloat(paymentHistory[0]?.total_amount) || 0;
  };

  const calculateDueAmount = () => {
    const totalAmount = getTotalAmount();
    const paidAmount = calculateTotalPaidAmount();
    return totalAmount - paidAmount;
  };

  return (
    <div className="container mt-4">
      <h4 className="mb-4 text-center">Settlement Details</h4>
      
      {message.text && (
        <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage({ text: '', type: '' })}></button>
        </div>
      )}
      
      <form onSubmit={updateSettlement} encType="multipart/form-data">
        <div className="table-responsive">
          <table className="table table-bordered table-striped">
            <thead className="bg-primary text-white">
              <tr>
                <th>Total Amount</th>
                <th>Previous Paid Amount</th>
                <th>Previous Due Amount</th>
                <th>Amount Paid</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <input 
                    type="text" 
                    className="form-control bg-light" 
                    value={getTotalAmount()} 
                    readOnly 
                  />
                </td>
                <td>
                  <input 
                    type="text" 
                    className="form-control bg-light" 
                    value={calculateTotalPaidAmount()} 
                    readOnly 
                  />
                </td>
                <td>
                  <input 
                    type="text" 
                    className="form-control bg-light" 
                    value={calculateDueAmount()} 
                    readOnly 
                  />
                </td>
                <td>
                  <input 
                    type="number" 
                    className="form-control" 
                    value={amount} 
                    onChange={handleAmountChange}
                    min="0"
                    max={calculateDueAmount()}
                    required
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="row mt-4">
          <div className="col-md-4 mb-3">
            <label htmlFor="serialNumber" className="form-label">Serial Number*</label>
            <input 
              type="text" 
              className="form-control" 
              id="serialNumber"
              name="serialNumber"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              required
            />
          </div>
          <div className="col-md-4 mb-3">
            <label htmlFor="attachment" className="form-label">Attachment</label>
            <input 
              type="file" 
              className="form-control" 
              id="attachment"
              ref={fileInputRef}
              onChange={handleFileChange}
            />
            {attachment && (
              <small className="text-muted">File: {attachment.name}</small>
            )}
          </div>
          <div className="col-md-4 mb-3">
            <label htmlFor="remarks" className="form-label">Remarks</label>
            <textarea 
              className="form-control" 
              id="remarks"
              name="remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows="2"
            ></textarea>
          </div>
        </div>
        
        {/* Payment History Table */}
        {paymentHistory.length > 0 && (
          <div className="mt-4">
            <h5>Payment History</h5>
            <div className="table-responsive">
              <table className="table table-sm table-bordered">
                <thead className="bg-light">
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Serial Number</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((payment, index) => (
                    <tr key={index}>
                      <td>{new Date(payment.payment_date).toLocaleDateString()}</td>
                      <td>{payment.amount_paid}</td>
                      <td>{payment.serial_number}</td>
                      <td>{payment.PaymentRemarks || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-end mt-3">
          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SettlementBlock;